import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetHSCodeSuggestionByNameQuery } from '@/hooks/queries/useGetHSCodeSuggestionByNameQuery';
import { EMPTY_ARRAY } from '@/constants/general';
import { HsCodeSuggestionApiResponse } from '@/api/category/types';
import { useCategoryHSCodeContext } from '../context/context';

export const useHSCodeSearchValidation = () => {
  const [searchValue6Digits, setSearchValue6Digits] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const { is10DigitCodeEnabled } = useCategoryHSCodeContext();

  const setSearchValueDebounced = useCallback((value: string) => {
    const valueToSearch = value.replace(/[^0-9]/g, '');
    setSearchValue(valueToSearch);
  }, []);

  const { data: hsCodeSuggestionsData, isLoading: isLoadingHSCodeSuggestions } =
    useGetHSCodeSuggestionByNameQuery(searchValue.substring(0, 8));

  const {
    data: hsCodeSuggestions6Digits = EMPTY_ARRAY,
    isLoading: isLoadingHSCodeSuggestions6Digits,
  } = useGetHSCodeSuggestionByNameQuery(searchValue6Digits);

  const refHsCodeSuggestions = useRef<HsCodeSuggestionApiResponse[]>(
    EMPTY_ARRAY as unknown as HsCodeSuggestionApiResponse[]
  );

  useEffect(() => {
    if (is10DigitCodeEnabled) {
      if (searchValue.length === 8 || searchValue.length === 10) {
        const hsCode6Digits = new Set(
          hsCodeSuggestions6Digits?.map((hsCode) => hsCode.name.substring(0, 6)) ?? []
        );
        const hsCode6Digit = searchValue.substring(0, 6);
        if (!hsCode6Digits.has(hsCode6Digit)) {
          setSearchValue6Digits(hsCode6Digit);
        }
      }
    }
  }, [is10DigitCodeEnabled, searchValue, hsCodeSuggestions6Digits]);

  const hsCodeSuggestions = useMemo(() => {
    if (searchValue?.length < 2) return EMPTY_ARRAY as unknown as HsCodeSuggestionApiResponse[];
    if (isLoadingHSCodeSuggestions) return refHsCodeSuggestions.current;
    if (hsCodeSuggestionsData?.length && searchValue.length < 9) return hsCodeSuggestionsData;
    if (searchValue.length < 7) return EMPTY_ARRAY as unknown as HsCodeSuggestionApiResponse[];
    if (is10DigitCodeEnabled) {
      if (searchValue.length === 8 || searchValue.length === 10) {
        const hsCode6Digits = new Set(
          hsCodeSuggestions6Digits?.map((hsCode) => hsCode.name.substring(0, 6)) ?? []
        );
        const hsCode6Digit = searchValue.substring(0, 6);
        if (!hsCode6Digits.has(hsCode6Digit)) {
          return EMPTY_ARRAY as unknown as HsCodeSuggestionApiResponse[];
        }
        return [
          {
            name: searchValue,
            description: '',
          },
          ...hsCodeSuggestions6Digits,
        ];
      }
    }
    return EMPTY_ARRAY as unknown as HsCodeSuggestionApiResponse[];
  }, [
    hsCodeSuggestionsData,
    searchValue,
    hsCodeSuggestions6Digits,
    isLoadingHSCodeSuggestions,
    is10DigitCodeEnabled,
  ]);

  useEffect(() => {
    refHsCodeSuggestions.current = hsCodeSuggestions;
  }, [hsCodeSuggestions]);

  const isLoadingHSCode =
    (searchValue.length > 1 && isLoadingHSCodeSuggestions) ||
    (searchValue6Digits.length > 1 && isLoadingHSCodeSuggestions6Digits);

  return {
    hsCodeSuggestions,
    isLoadingHSCodeSuggestions: isLoadingHSCode,
    setSearchValueDebounced,
    searchValue,
  };
};
